// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName } from '@medplum/core';
import type { Patient, Practitioner, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  CLOUDPAT_HSAT_CODING,
  HSAT_ORDERED_DATE_EXTENSION_URL,
  HSAT_STATUS_LABELS,
  HSAT_STATUS_TAG_SYSTEM,
  TAG_SYSTEM,
} from '@polarissleep/shared';

import { useCallback, useEffect, useState } from 'react';

export const HSAT_DEVICE_STATUS_SYSTEM = 'https://polarissleep.com/hsat-device-status';

export type HsatDeviceStatus =
  | 'patient-approval-pending'
  | 'hsat-ordered'
  | 'hsat-partial-results'
  | 'hsat-final-results';

export const HSAT_DEVICE_STATUS_LABELS: Record<HsatDeviceStatus, string> = {
  'patient-approval-pending': 'Patient Approval Pending',
  'hsat-ordered': 'Ordered',
  'hsat-partial-results': 'Partial Results Available',
  'hsat-final-results': 'Final Results Available',
};

export interface HsatDeviceEntry {
  serviceRequestId: string;
  patientId: string;
  patientName: string;
  requesterId: string | undefined;
  orderedDate: string | undefined;
  deviceStatus: HsatDeviceStatus | null;
  hsatStatusDisplay: string | undefined;
  requesterName: string | undefined;
  serviceRequest: ServiceRequest;
  // Active test fields
  dateArrived: string | undefined;
  activatedDate: string | undefined;
  lastContact: string | undefined;
  serialNumber: string | undefined;
}

interface UseHSATDevicesReturn {
  entries: HsatDeviceEntry[];
  loading: boolean;
  refresh: () => void;
  updateEntryStatus: (serviceRequestId: string, newStatus: HsatDeviceStatus) => void;
}

function extractDeviceStatus(sr: ServiceRequest): HsatDeviceStatus | null {
  const tag = sr.meta?.tag?.find((t) => t.system === HSAT_STATUS_TAG_SYSTEM);
  return (tag?.code as HsatDeviceStatus | undefined) ?? null;
}

function buildEntry(
  sr: ServiceRequest,
  patientMap: Map<string, Patient>,
  practitionerMap: Map<string, Practitioner>
): HsatDeviceEntry {
  const patientRef = sr.subject?.reference;
  const patientId = patientRef?.split('/')[1] ?? '';
  const patient = patientId ? patientMap.get(patientId) : undefined;
  const patientName = patient?.name?.[0] ? formatHumanName(patient.name[0]) : 'Unknown Patient';

  const requesterRef = sr.requester?.reference;
  const practitionerId = requesterRef?.split('/')[1] ?? '';
  const practitioner = practitionerId ? practitionerMap.get(practitionerId) : undefined;
  const requesterName = practitioner?.name?.[0] ? formatHumanName(practitioner.name[0]) : undefined;

  return {
    serviceRequestId: sr.id ?? '',
    patientId,
    patientName,
    requesterId: practitionerId || undefined,
    orderedDate: sr.extension?.find((e) => e.url === HSAT_ORDERED_DATE_EXTENSION_URL)?.valueDateTime ?? sr.authoredOn,
    deviceStatus: extractDeviceStatus(sr),
    hsatStatusDisplay: (() => {
      const code = sr.meta?.tag?.find((t) => t.system === HSAT_STATUS_TAG_SYSTEM || t.system === TAG_SYSTEM)?.code;
      return code ? HSAT_STATUS_LABELS[code] : undefined;
    })(),
    requesterName,
    serviceRequest: sr,
    dateArrived: undefined,
    activatedDate: undefined,
    lastContact: undefined,
    serialNumber: undefined,
  };
}

export function useHSATDevices(): UseHSATDevicesReturn {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<HsatDeviceEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const serviceRequests = await medplum.searchResources('ServiceRequest', {
        code: `${CLOUDPAT_HSAT_CODING.system}|${CLOUDPAT_HSAT_CODING.code}`,
        _count: '1000',
        _sort: '-_lastUpdated',
      });

      if (serviceRequests.length === 0) {
        setEntries([]);
        return;
      }

      const patientIds = [
        ...new Set(
          serviceRequests
            .map((sr) => sr.subject?.reference?.split('/')[1])
            .filter((id): id is string => id !== undefined)
        ),
      ];
      const practitionerIds = [
        ...new Set(
          serviceRequests
            .map((sr) => sr.requester?.reference?.split('/')[1])
            .filter((id): id is string => id !== undefined)
        ),
      ];

      const [patients, practitioners] = await Promise.all([
        patientIds.length > 0
          ? medplum.searchResources('Patient', { _id: patientIds.join(','), _count: '1000' })
          : Promise.resolve([] as Patient[]),
        practitionerIds.length > 0
          ? medplum.searchResources('Practitioner', { _id: practitionerIds.join(','), _count: '1000' })
          : Promise.resolve([] as Practitioner[]),
      ]);

      const patientMap = new Map(patients.map((p) => [p.id ?? '', p]));
      const practitionerMap = new Map(practitioners.map((p) => [p.id ?? '', p]));

      setEntries(serviceRequests.map((sr) => buildEntry(sr, patientMap, practitionerMap)));
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  useEffect(() => {
    fetchEntries().catch(console.error);
  }, [fetchEntries]);

  const updateEntryStatus = useCallback(
    (serviceRequestId: string, newStatus: HsatDeviceStatus): void => {
      setEntries((prev) =>
        prev.map((e) => {
          if (e.serviceRequestId !== serviceRequestId) {
            return e;
          }
          const updatedTags = (e.serviceRequest.meta?.tag ?? [])
            .filter((t) => t.system !== HSAT_DEVICE_STATUS_SYSTEM)
            .concat({
              system: HSAT_DEVICE_STATUS_SYSTEM,
              code: newStatus,
              display: HSAT_DEVICE_STATUS_LABELS[newStatus],
            });
          const updatedSr: ServiceRequest = {
            ...e.serviceRequest,
            meta: { ...e.serviceRequest.meta, tag: updatedTags },
          };
          medplum.updateResource(updatedSr).catch(console.error);
          return { ...e, deviceStatus: newStatus, serviceRequest: updatedSr };
        })
      );
    },
    [medplum]
  );

  return { entries, loading, refresh: fetchEntries, updateEntryStatus };
}
